import QRCode from "qrcode";
import { blobToDataUrl } from "./eventPassQr";
import invitationTemplate from "../assets/images/invitation-card-template.jpg";

const INK = "#5a4633";
const ACCENT = "#8a6a3d";
const MUTED = "#7a6550";

const wrapCanvasText = (ctx, text, maxWidth) => {
  const words = String(text || "")
    .split(/\s+/)
    .filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [];
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

let templateImagePromise = null;
const getTemplateImage = () => {
  if (!templateImagePromise) {
    templateImagePromise = loadImage(invitationTemplate);
  }
  return templateImagePromise;
};

export const blobToBase64 = async (blob) => {
  const dataUrl = await blobToDataUrl(blob);
  const parts = String(dataUrl).split(",");
  return parts[1] || "";
};

const formatInvitationDateTime = (dateValue, timeValue = "") => {
  const datePart = String(dateValue || "").trim();
  const timePart = String(timeValue || "").trim();
  if (!datePart && !timePart) return "";
  let prettyDate = datePart;
  if (datePart) {
    const parsed = new Date(String(datePart).split("T")[0]);
    if (!Number.isNaN(parsed.getTime())) {
      prettyDate = parsed.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  }
  return [prettyDate, timePart].filter(Boolean).join(", ");
};

const formatInvitationVenue = (occasion) => {
  if (!occasion) return "";
  const address = occasion.venueAddress?.address;
  const addressLine =
    typeof address === "string"
      ? address.trim()
      : address && typeof address === "object"
        ? [
            address.line1,
            address.line2,
            address.line3,
            address.city,
            address.state,
            address.pincode,
            address.country,
          ]
            .filter(Boolean)
            .join(", ")
        : "";
  return [occasion.venueAddress?.name || "", addressLine]
    .filter(Boolean)
    .join(", ");
};

export const mapOccasionToInvitationRow = (occasion) => {
  if (!occasion) return null;
  return {
    name: occasion.OccasionName || "",
    dateTime: formatInvitationDateTime(occasion.Date, occasion.time),
    venue: formatInvitationVenue(occasion),
    dressCode: String(occasion.DressCode || "").trim(),
  };
};

/**
 * Fills the blank wedding template with guest, occasion, venue, and QR,
 * centered in the inner cream frame.
 */
export const generateInvitationCardBlob = async ({
  guestName,
  eventName,
  hostedBy = "",
  occasions = [],
  qrPayload = "",
} = {}) => {
  const template = await getTemplateImage();
  const width = template.naturalWidth || 1080;
  const height = template.naturalHeight || 1920;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(template, 0, 0, width, height);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const centerX = width / 2;
  const maxTextW = width * 0.5;
  const innerTop = height * 0.2;
  const innerBottom = height * 0.8;
  const innerHeight = innerBottom - innerTop;

  const name = String(guestName || "Guest").trim();
  const occasionRows = (occasions || [])
    .filter((row) => row && (row.name || row.dateTime || row.venue))
    .slice(0, 3);

  const textItem = (text, font, color, lineHeight, maxLines = 4) => {
    ctx.font = font;
    const lines = wrapCanvasText(ctx, text, maxTextW).slice(0, maxLines);
    return { type: "text", lines, font, color, lineHeight };
  };

  const gap = (size) => ({ type: "gap", size });

  const items = [
    textItem(
      `DEAR ${name.toUpperCase()}`,
      `600 ${Math.round(width * 0.028)}px Georgia, 'Times New Roman', serif`,
      ACCENT,
      width * 0.038,
      2
    ),
    gap(height * 0.012),
    textItem(
      "You are cordially invited to",
      `400 ${Math.round(width * 0.022)}px Helvetica, Arial, sans-serif`,
      MUTED,
      width * 0.032,
      2
    ),
    gap(height * 0.008),
    textItem(
      eventName || "our celebration",
      `700 ${Math.round(width * 0.036)}px Georgia, 'Times New Roman', serif`,
      INK,
      width * 0.046,
      3
    ),
    gap(height * 0.016),
  ];

  occasionRows.forEach((row, index) => {
    if (index) items.push(gap(height * 0.01));
    if (row.name) {
      items.push(
        textItem(
          String(row.name),
          `700 ${Math.round(width * 0.024)}px Georgia, 'Times New Roman', serif`,
          ACCENT,
          width * 0.032,
          2
        )
      );
    }
    if (row.dateTime) {
      items.push(
        textItem(
          `Date & Time: ${row.dateTime}`,
          `600 ${Math.round(width * 0.02)}px Helvetica, Arial, sans-serif`,
          INK,
          width * 0.028,
          2
        )
      );
    }
    if (row.venue) {
      items.push(
        textItem(
          `Venue: ${row.venue}`,
          `400 ${Math.round(width * 0.018)}px Helvetica, Arial, sans-serif`,
          MUTED,
          width * 0.026,
          3
        )
      );
    }
    if (row.dressCode) {
      items.push(
        textItem(
          `Dress code: ${row.dressCode}`,
          `400 ${Math.round(width * 0.018)}px Helvetica, Arial, sans-serif`,
          MUTED,
          width * 0.026,
          2
        )
      );
    }
  });

  let qrImg = null;
  let qrSize = 0;
  if (qrPayload) {
    qrSize = Math.round(width * 0.22);
    const qrDataUrl = await QRCode.toDataURL(String(qrPayload), {
      width: qrSize,
      margin: 1,
      errorCorrectionLevel: "H",
      color: { dark: "#3d2c1e", light: "#fffdf8" },
    });
    qrImg = await loadImage(qrDataUrl);
    items.push(gap(height * 0.016));
    items.push({ type: "qr", size: qrSize });
    items.push(gap(height * 0.014));
    items.push(
      textItem(
        "THIS QR CODE IS NON-TRANSFERABLE",
        `600 ${Math.round(width * 0.016)}px Helvetica, Arial, sans-serif`,
        MUTED,
        width * 0.024,
        2
      )
    );
    items.push(
      textItem(
        "Kindly present this QR code for your entry at the venue",
        `400 ${Math.round(width * 0.016)}px Helvetica, Arial, sans-serif`,
        MUTED,
        width * 0.024,
        2
      )
    );
  }

  if (hostedBy) {
    items.push(gap(height * 0.01));
    items.push(
      textItem(
        `Warmly, ${hostedBy}`,
        `400 ${Math.round(width * 0.018)}px Georgia, 'Times New Roman', serif`,
        ACCENT,
        width * 0.026,
        2
      )
    );
  }

  const itemHeight = (item) => {
    if (item.type === "gap") return item.size;
    if (item.type === "qr") return item.size;
    return item.lines.length * item.lineHeight;
  };

  let contentHeight = items.reduce((sum, item) => sum + itemHeight(item), 0);
  if (contentHeight > innerHeight) {
    const scale = innerHeight / contentHeight;
    items.forEach((item) => {
      if (item.type === "gap") item.size *= scale;
      if (item.type === "qr") item.size *= scale;
      if (item.type === "text") item.lineHeight *= scale;
    });
    contentHeight = innerHeight;
  }

  let y = innerTop + (innerHeight - contentHeight) / 2;

  items.forEach((item) => {
    if (item.type === "gap") {
      y += item.size;
      return;
    }
    if (item.type === "qr" && qrImg) {
      const drawSize = item.size;
      ctx.drawImage(qrImg, centerX - drawSize / 2, y, drawSize, drawSize);
      y += drawSize;
      return;
    }
    ctx.fillStyle = item.color;
    ctx.font = item.font;
    item.lines.forEach((line) => {
      ctx.fillText(line, centerX, y + item.lineHeight / 2);
      y += item.lineHeight;
    });
  });

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not create invitation card"));
      },
      "image/png",
      0.92
    );
  });
};
